import React, {
  useCallback, useState, Fragment, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import PaddedFlexContainer from '../../../../components/padded-flex-container/padded-flex-container.component';
import Grid from '../../../../components/grid/grid.component';
import GridItem from '../../../../components/grid-item/grid-item.component';
import Button from '../../../../components/button/button.component';
import TextHeader from '../../../../components/text-header/text-header.component';
import TextBlock from '../../../../components/text-block/text-block.component';
import ErrorBlock from '../../../../components/error-block/error-block.component';
import FilteredLink from '../../../../components/filtered-link/filtered-link.component';
import Form from '../../../../components/form/form.component';
import FormContainer from '../../../../components/form-container/form-container.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import ForgotPasswordValidator from './forgot-password.validator';
import FormService from '../../../../services/form-service';
import useServerFormErrors from '../../../../hooks/use-server-form-errors';
import useFormStructureModified from '../../../../hooks/use-form-structure-modified';
import { resetFormErrors } from '../../app.action';
import recoverPassword from './forgot-password.action';
import { FORM_ID_FORGOT_PASSWORD } from '../../../../constants/system/form-ids';
import { ROUTE_DASHBOARD, ROUTE_LOGIN } from '../../../../constants/system/routes';

const ForgotPassword: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    currentUser,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const dispatch: ReduxDispatch = useDispatch();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, true);

  const [isFormSent, setIsFormSent]: [boolean, Function] = useState(false);

  const isSaveDisabled: boolean = isFormSaving || (isFormPristine && !isFormStructureModified);
  const withServerErrors: boolean = !!serverFormErrors && !!Object.keys(serverFormErrors).length;

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(recoverPassword({
        recoverPasswordForm: {
          email: values.email,
        },
      }, (response: ActionResponse): void => {
        setIsFormSent(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, []);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [withFormErrors, formValues]);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  if (currentUser && currentUser.id) {
    return <Navigate to={ROUTE_DASHBOARD} />;
  }

  return (
    <Form withAutocomplete id={FORM_ID_FORGOT_PASSWORD} onChange={onFormChange}>
      <FlexContainer isCentered>
        <PaddedFlexContainer isLarge isColumn isTextCenter width={900}>
          <Grid withLargeSpacing>
            <GridItem isFullWidth>
              <TextHeader>
                Password Recovery
              </TextHeader>
              {
                isFormSent ? (
                  <TextBlock>
                    A password change link has been sent to your e-mail address
                  </TextBlock>
                ) : (
                  <TextBlock>
                    Please enter an user account email
                  </TextBlock>
                )
              }
            </GridItem>
            {
              !isFormSent && (
                <Fragment>
                  <GridItem isFullWidth>
                    <FormFieldText isOutlined name="email" label="Email" />
                  </GridItem>
                  <GridItem isFullWidth>
                    <Button
                      isActive
                      isDefault
                      isExtended
                      isDisabled={isSaveDisabled}
                      onClick={onSubmitForm}
                    >
                      Send
                    </Button>
                  </GridItem>
                  <GridItem isFullWidth>
                    <FilteredLink link={ROUTE_LOGIN}>
                      Sign In
                    </FilteredLink>
                  </GridItem>
                </Fragment>
              )
            }
          </Grid>
          {
            (!!formError || !!mainServerFormError) && !isFormSaving && (
              <ErrorBlock errorMessage={formError || mainServerFormError} />
            )
          }
        </PaddedFlexContainer>
      </FlexContainer>
    </Form>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
  } = reducers;

  initialValues.email = null;

  return {
    currentUser: app.baseData && app.baseData.currentUser,
  };
};

const ForgotPasswordForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ forgotPassword }: Reducers): ValidationErrorsMap => (
    forgotPassword.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ forgotPassword }: Reducers): ValidationErrorsMap => (
    forgotPassword.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_FORGOT_PASSWORD}
      component={ForgotPassword}
      validator={ForgotPasswordValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(ForgotPasswordForm);
